import { Button, Dialog, Typography, cn, formatNumber } from "@tosize/ui";
import { RadioGroup } from "radix-ui";
import { useState } from "react";
import { useIntl } from "react-intl";
import type { OuterConstraintsType } from "../../context/StoreContext";
import { useCabinetStore } from "../../context/StoreContext";
import TitleBlock from "../TitleBlock";
import { getCurrentMaterial, isLongPartsBuildMethod } from "../renderer/data";

export default function Thickness() {
	const intl = useIntl();
	const { state, api, setThickness, getOuterConstraints } = useCabinetStore();
	type DialogType = { constraints: OuterConstraintsType; thickness: number } | false;
	const [dialog, setDialog] = useState<DialogType>(false);

	const material = getCurrentMaterial(state, api);

	if (material === undefined) {
		return (
			<div className="animate-pulse">
				<div className="mb-2 flex w-full items-center justify-between border-shades border-b pb-2">
					<div className="h-5 w-1/2 rounded bg-shades" />
				</div>

				<div className="mt-1 h-[44px] w-full rounded-full bg-shades" />
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-y-1.5">
			<TitleBlock
				title={intl.formatMessage({ defaultMessage: "Dikte" })}
				label={{
					title: intl.formatMessage({ defaultMessage: "Nieuwe diktes" }),
				}}
			/>

			{material?.thicknesses?.length === 1 ? (
				<div className="flex items-center justify-between rounded-full bg-white">
					<div className="mx-1 w-full whitespace-nowrap rounded-full border-2 border-transparent bg-primary-medium p-2 xs:px-3 text-center text-sm text-white xs:text-base xs:leading-none first:ml-0 last:mr-0 xl:px-3 xl:text-base xl:leading-none">
						{formatNumber({
							lang: intl.locale,
							value: Number(material.thicknesses.at(0)?.name),
							unit: "millimeter",
							digits: { min: 0, max: 0 },
						})}
					</div>
				</div>
			) : (
				<>
					<RadioGroup.Root
						value={String(state.thickness)}
						onValueChange={(value) => {
							const newThickness = Number(value);
							const outerConstraints = getOuterConstraints(state.material, newThickness);
							const { width, height, depth } = outerConstraints;

							if (
								isLongPartsBuildMethod(newThickness) &&
								(state.width > width.max || state.height > height.max || state.depth > depth.max)
							) {
								return setDialog({ constraints: outerConstraints, thickness: newThickness });
							}

							return setThickness(state, newThickness);
						}}
						orientation="horizontal"
						className="flex items-center justify-between rounded-full bg-white"
					>
						{material?.thicknesses?.map((item) => {
							if (!item) return null;

							return (
								<RadioGroup.Item
									value={item.name}
									key={item.name}
									className={cn(
										"mx-1 w-full whitespace-nowrap rounded-full border-2 border-transparent bg-white p-2 text-sm first:ml-0 last:mr-0 focus:outline-none focus-visible:bg-primary-dark motion-safe:transition",
										"xs:px-3 xs:text-base xs:leading-none",
										"xl:px-2 xl:text-sm xl:leading-none",
										"xl:px-3 xl:text-base xl:leading-none",
										"hover:border-primary-dark",
										"disabled:cursor-not-allowed disabled:border-transparent disabled:bg-white disabled:text-shades-medium",
										"rdx-state-checked:bg-primary-medium rdx-state-checked:text-white",
										"hover:rdx-state-checked:bg-primary-dark focus-visible:rdx-state-checked:bg-primary-dark",
									)}
								>
									{formatNumber({
										lang: intl.locale,
										value: Number(item.name),
										unit: "millimeter",
										digits: { min: 0, max: 0 },
									})}
								</RadioGroup.Item>
							);
						})}
					</RadioGroup.Root>

					<Dialog
						width="xl"
						open={typeof dialog === "object"}
						onOpenChange={() => setDialog(false)}
						title="Weet je zeker dat je de dikte wilt wijzigen?"
					>
						{typeof dialog === "object" && (
							<>
								<Typography>
									{intl.formatMessage({
										defaultMessage:
											"Je kast is groter dan de maximale afmetingen voor de geselecteerde dikte.",
									})}

									<br />

									{intl.formatMessage({
										defaultMessage: "De maximale afmetingen zijn:",
									})}
									{` ${formatNumber({
										value: dialog.constraints.width.max / 10,
										style: "decimal",
										lang: intl.locale,
										digits: { min: 0, max: 0 },
									})} x ${formatNumber({
										value: dialog.constraints.height.max / 10,
										style: "decimal",
										lang: intl.locale,
										digits: { min: 0, max: 0 },
									})} x ${formatNumber({
										value: dialog.constraints.depth.max / 10,
										unit: "centimeter",
										lang: intl.locale,
										digits: { min: 0, max: 0 },
									})}`}

									<br />

									{intl.formatMessage(
										{
											defaultMessage:
												"Als je de dikte wijzigt naar {value}, zullen de afmetingen van de kast worden aangepast.",
										},
										{
											value: formatNumber({
												value: dialog.thickness,
												unit: "millimeter",
												lang: intl.locale,
												digits: { min: 0, max: 0 },
											}),
										},
									)}
								</Typography>

								<div className="mt-4 flex flex-wrap justify-end gap-x-4 gap-y-2">
									<Button variant="link" onClick={() => setDialog(false)}>
										{intl.formatMessage({
											defaultMessage: "Nee, huidige dikte behouden",
										})}
									</Button>
									<Button
										onClick={() => {
											setThickness(state, dialog.thickness);
											setDialog(false);
										}}
									>
										{intl.formatMessage({ defaultMessage: "Ja, dikte wijzigen" })}
									</Button>
								</div>
							</>
						)}
					</Dialog>
				</>
			)}
		</div>
	);
}
